import type { EventSearchSummary } from '@src/models/interfaces/event.interface';
import type { ExecutionSearchSummary } from '@src/models/interfaces/execution.interface';
import { ScheduleType } from '@src/models/interfaces/schedule.interface';
import type { ScheduleSearchSummary } from '@src/models/interfaces/schedule.interface';

import type {
  MenuSearchItem,
  SearchEntry,
  SidebarSearchUser,
} from '../components/SidebarSearch/types';

export interface BuildSearchEntriesParams {
  menuItems: MenuSearchItem[];
  users: SidebarSearchUser[];
  events: EventSearchSummary[];
  schedules: ScheduleSearchSummary[];
  tasks: ExecutionSearchSummary[];
}

const toScheduleEntries = (
  schedules: ScheduleSearchSummary[],
  type: ScheduleType,
  entryType: 'schedule' | 'route',
): SearchEntry[] =>
  schedules
    .filter(schedule => schedule.type === type)
    .map(schedule => ({
      key: `${entryType}-${schedule.divisionId}-${schedule.id}`,
      type: entryType,
      schedule,
    }));

/** Visible search rows in display order: menu, users, events, schedules, routes, tasks. */
export const buildSearchEntries = ({
  menuItems,
  users,
  events,
  schedules,
  tasks,
}: BuildSearchEntriesParams): SearchEntry[] => [
  ...menuItems.map(item => ({
    key: `menu-${item.key}`,
    type: 'menu' as const,
    item,
  })),
  ...users.map(user => ({
    key: `user-${user.id}`,
    type: 'user' as const,
    user,
  })),
  ...events.map(event => ({
    key: `event-${event.division.id}-${event.id}`,
    type: 'event' as const,
    event,
  })),
  ...toScheduleEntries(schedules, ScheduleType.Schedule, 'schedule'),
  ...toScheduleEntries(schedules, ScheduleType.CheckpointRoute, 'route'),
  ...tasks.map(task => ({
    key: `task-${task.division.id}-${task.id}`,
    type: 'task' as const,
    task,
  })),
];
